/**
 * Shared DTA display utilities. Use these everywhere a DTA is displayed to ensure consistent naming
 * and status rendering.
 */

import type Badge from "@cloudscape-design/components/badge"
import type { ComponentProps } from "react"
import type { WorkflowStage } from "@/generated/sdk"

type BadgeColor = ComponentProps<typeof Badge>["color"]

/**
 * Badge colour per workflow stage, shared across every DTA listing so stages read consistently.
 */
export const STATUS_COLOR: Record<WorkflowStage, BadgeColor> = {
  DRAFT: "grey",
  REVIEW: "blue",
  APPROVED: "green",
  ACTIVE: "green",
  INACTIVE: "red",
}

/**
 * Badge colour for a stage that may be undefined or off-enum; falls back to grey.
 */
export function stageBadgeColor(stage: string | undefined): BadgeColor {
  return (stage ? STATUS_COLOR[stage as WorkflowStage] : undefined) ?? "grey"
}

export interface DTADisplayFields {
  dta_id: string
  study_id?: string
  vendor_name?: string
  vendor_id?: string
  source_type?: string
  domains?: string[]
  version?: string
  workflow_stage?: string
  created_at?: string
  last_modified_at?: string
}

/**
 * Returns a human-readable display name for a DTA. Third-party DTAs: "{Vendor} - {Domain}
 * v{Version}" (e.g., "LabCorp - Laboratory v2") EDC DTAs: "{Domain} v{Version}" (e.g., "Personal
 * History of Cancer v1.0")
 */
export function getDTADisplayName(dta: DTADisplayFields): string {
  const domains = dta.domains?.length ? dta.domains.join(", ") : ""
  const version = dta.version ? ` v${dta.version}` : ""

  if (dta.source_type === "edc" || dta.source_type === "sdtm") {
    const fallback = dta.source_type === "edc" ? "EDC" : "SDTM"
    return `${domains || fallback}${version}`
  }

  const vendor = dta.vendor_name
  return domains ? `${vendor} - ${domains}${version}` : `${vendor}${version}`
}

/**
 * Returns the effective display status for a DTA. Normalizes workflow_stage into a user-friendly
 * label.
 */
export function getDTADisplayStatus(dta: DTADisplayFields): string {
  const stage = dta.workflow_stage
  if (!stage) return "UNKNOWN"
  return stage.replace(/_/g, " ")
}

/**
 * Formats a date string with time including hours and minutes.
 */
export function formatDTADateTime(dateStr?: string): string {
  if (!dateStr) return "-"
  const date = new Date(dateStr)
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
